import React, { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NominationsPage = () => {
  const [formData, setFormData] = useState({
    yourName: '',
    yourEmail: '',
    nomineeName: '',
    nomineeEmail: '',
    organization: '',
    reason: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await axios.post(`${API}/nominations`, {
        your_name: formData.yourName,
        your_email: formData.yourEmail,
        nominee_name: formData.nomineeName,
        nominee_email: formData.nomineeEmail,
        organization: formData.organization,
        reason: formData.reason
      });
      
      setFormSubmitted(true);
      setFormData({
        yourName: '',
        yourEmail: '',
        nomineeName: '',
        nomineeEmail: '',
        organization: '',
        reason: ''
      });
    } catch (err) {
      console.error('Error submitting nomination:', err);
      setError('Failed to submit nomination. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-5xl mb-6">🖖</div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-white">Nominate a </span>
            <span className="text-amber-500">Future Honoree</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Know someone who embodies the spirit of "Live Long and Prosper"? We're looking for autistic leaders and allies committed to creating a world that embraces neurodiversity and belonging for all.
          </p>
        </div>
      </section>

      {/* Criteria Section */}
      <section className="py-16 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Who We're Looking For</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#141416] border border-gray-800/50 rounded-xl p-6 text-center">
              <div className="text-3xl mb-4">🌟</div>
              <h3 className="text-white font-semibold mb-2">Community Leaders</h3>
              <p className="text-gray-400 text-sm">Individuals making extraordinary contributions to their communities</p>
            </div>
            <div className="bg-[#141416] border border-gray-800/50 rounded-xl p-6 text-center">
              <div className="text-3xl mb-4">💡</div>
              <h3 className="text-white font-semibold mb-2">Autism Advocates</h3>
              <p className="text-gray-400 text-sm">Champions of neurodiversity and autism awareness</p>
            </div>
            <div className="bg-[#141416] border border-gray-800/50 rounded-xl p-6 text-center">
              <div className="text-3xl mb-4">🤝</div>
              <h3 className="text-white font-semibold mb-2">Inclusion Champions</h3>
              <p className="text-gray-400 text-sm">Those promoting diversity and inclusion in all spaces</p>
            </div>
          </div>
        </div>
      </section>

      {/* Nomination Form */}
      <section className="py-24 bg-[#0a0a0b]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#141416] border border-gray-800/50 rounded-2xl p-8 md:p-10">
            {formSubmitted ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-6">🖖</div>
                <h3 className="text-2xl font-bold text-white mb-4">Thank You!</h3>
                <p className="text-gray-400 mb-6">
                  Your nomination has been received. We appreciate your commitment to recognizing those who embody the spirit of "Live Long and Prosper."
                </p>
                <button
                  onClick={() => setFormSubmitted(false)}
                  className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all"
                >
                  Submit Another Nomination
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                    <p className="text-red-500">{error}</p>
                  </div>
                )}

                {/* Your Information */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    Your Information
                  </h3>
                  <div className="space-y-4 pl-10">
                    <div>
                      <label htmlFor="yourName" className="block text-sm text-gray-400 mb-2">Your Name *</label>
                      <input
                        type="text"
                        id="yourName"
                        name="yourName"
                        value={formData.yourName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-colors"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="yourEmail" className="block text-sm text-gray-400 mb-2">Your Email *</label>
                      <input
                        type="email"
                        id="yourEmail"
                        name="yourEmail"
                        value={formData.yourEmail}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-colors"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Nominee Information */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    Nominee Information
                  </h3>
                  <div className="space-y-4 pl-10">
                    <div>
                      <label htmlFor="nomineeName" className="block text-sm text-gray-400 mb-2">Nominee's Name *</label>
                      <input
                        type="text"
                        id="nomineeName"
                        name="nomineeName"
                        value={formData.nomineeName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 transition-colors"
                        placeholder="Enter nominee's full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="nomineeEmail" className="block text-sm text-gray-400 mb-2">Nominee's Email *</label>
                      <input
                        type="email"
                        id="nomineeEmail"
                        name="nomineeEmail"
                        value={formData.nomineeEmail}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 transition-colors"
                        placeholder="nominee@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="organization" className="block text-sm text-gray-400 mb-2">Organization</label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 transition-colors"
                        placeholder="Company or organization (optional)"
                      />
                    </div>
                    <div>
                      <label htmlFor="reason" className="block text-sm text-gray-400 mb-2">Why are you nominating this person? *</label>
                      <textarea
                        id="reason"
                        name="reason"
                        value={formData.reason}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 bg-[#1a1a1c] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-amber-500 transition-colors resize-none"
                        placeholder="Tell us about their contributions to the community, their advocacy work, and why they embody the spirit of 'Live Long and Prosper'..."
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Nomination'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-amber-500 text-6xl font-serif mb-6">"</div>
          <blockquote className="text-2xl md:text-3xl font-light text-white leading-relaxed mb-8">
            The miracle is this: the more we share, the more we have.
          </blockquote>
          <p className="text-amber-500 text-lg">
            — <span className="italic">Leonard Nimoy</span>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NominationsPage;
